import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Space, Spin, Typography } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import { useAggregationConfig } from '../../../hooks/SimViewer/useAggregationConfig';
import { useLinkAggregationFilter } from '../../../hooks/SimViewer/useLinkAggregationFilter';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useDataFilter } from '../../../hooks/SimViewer/useDataFilter';
import { useLinkAggregationData } from '../../../hooks/data/useLinkAggregationData';
import { isSameRange } from '../../../utils/range';
import { calcMean, calcMedian } from '../../../utils/statistics';
import { formatCount } from '../../../utils/format';
import { createHistogram } from '../../../utils/chart';
import { HistogramChart } from '../Chart/HistogramChart';
import { MapMarkedIconButton } from '../../MapMarkedIconButton';

type Props = {
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

const TrafficVolumePanel = ({ icon, label, extra }: Props): ReactNode => {
  const { aggregationConfig, startAggregation, clearAggregation } =
    useAggregationConfig();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isAggregating = aggregationConfig?.type === 'links';

  return (
    <Collapse
      bordered={false}
      activeKey={isOpen ? ['traffic-volume'] : []}
      expandIcon={(): ReactNode => {
        return isAggregating ? <UpOutlined rotate={isOpen ? 0 : 180} /> : null;
      }}
      expandIconPosition="right"
      onChange={(key: string | string[]): void => {
        if (
          Array.isArray(key) &&
          key.includes('traffic-volume') &&
          isAggregating
        ) {
          setIsOpen(true);
        } else {
          setIsOpen(false);
        }
      }}
    >
      <Collapse.Panel
        key="traffic-volume"
        header={
          <Space>
            {icon}
            {label}
            {extra}
          </Space>
        }
        extra={
          <MapMarkedIconButton
            active={isAggregating}
            onClick={() => {
              if (isAggregating) {
                clearAggregation();
                setIsOpen(false);
              } else {
                startAggregation({ type: 'links' });
              }
            }}
          />
        }
      >
        {isAggregating && <TrafficVolumePanelContent />}
      </Collapse.Panel>
    </Collapse>
  );
};

const TrafficVolumePanelContent = (): ReactNode => {
  const { t } = useTranslation();
  const { dataFilter } = useDataFilter();
  const { aggregations, isLoading } = useLinkAggregationData();
  const { linkAggregationFilter, setLinkAggregationFilter } =
    useLinkAggregationFilter();
  const { currentDataMeta } = useMetaData();
  const passengerFactor = Number(currentDataMeta?.passenger_factor ?? 1);

  const isTripByCar =
    dataFilter.longestDistanceModes.length === 1 &&
    dataFilter.longestDistanceModes[0] === 'car';
  const values = Array.from(aggregations).map(
    ([, value]: [string, number]): number => {
      return isTripByCar ? Math.floor(value / passengerFactor) : value;
    }
  );
  const histogramData = values.length > 0 ? createHistogram(values, 20) : [];

  return (
    <Spin spinning={isLoading}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space>
          <Typography.Text>
            {t('mean')}：
            {values.length > 0
              ? formatCount(
                  calcMean(values),
                  isTripByCar ? t('unit.car') : t('unit.agent')
                )
              : '-'}
          </Typography.Text>
          <Typography.Text>
            {t('median')}：
            {values.length > 0
              ? formatCount(
                  calcMedian(values),
                  isTripByCar ? t('unit.car') : t('unit.agent')
                )
              : '-'}
          </Typography.Text>
        </Space>
        <HistogramChart
          data={histogramData}
          rangeType="count"
          noDataMessage={t('message.noDataFound')}
          filterRange={linkAggregationFilter}
          onFilterRangeChange={(filterRange: [number, number] | null): void => {
            if (!isSameRange(filterRange, linkAggregationFilter)) {
              setLinkAggregationFilter(filterRange);
            }
          }}
        />
      </Space>
    </Spin>
  );
};

export { TrafficVolumePanel };
